import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button, FormControl, FormHelperText, Grid, Typography, useMediaQuery, MenuItem } from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import 'utils/YupHelper.js';
import { CustomInput, CustomSelect } from 'views/utilities/CustomComponent';
import SnackAlert from 'views/utilities/SnackAlert';
import useScriptRef from 'hooks/useScriptRef';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { AuthHeader, ApiUrl } from 'utils/Constants';
import { FetchData, SetError } from 'utils/FetchData';
import { SetUserData } from 'utils/User';

// ===========================|| FIREBASE - PROFILE ||=========================== //

const FirebaseProfile = ({ ...others }) => {
    const theme = useTheme();
    const scriptedRef = useScriptRef();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const ref = useRef();

    useEffect(() => {
        const getUser = async () => {
            const res = await fetch(`${ApiUrl}/api/user/profile`, {
                method: 'GET',
                headers: AuthHeader
            });
            // console.log(res);
            const response = await res.json();
            const data = response.data;

            ref.current.setFieldValue('firstName', data?.first_name || '');
            ref.current.setFieldValue('lastName', data?.last_name || '');
            ref.current.setFieldValue('mobile', data?.mobile);
            ref.current.setFieldValue('gender', data?.gender || 'Male');
            ref.current.setFieldValue('email', data?.email || '');
        };

        getUser();
    }, []);

    return (
        <>
            <Formik
                innerRef={ref}
                initialValues={{
                    mobile: '',
                    firstName: '',
                    lastName: '',
                    gender: 'Male',
                    email: '',
                    submit: null
                }}
                validationSchema={Yup.object().shape({
                    firstName: Yup.string().min(3, 'حداقل 3 حرف وارد نمایید').max(255).required('نام الزامی است'),
                    lastName: Yup.string().min(3, 'حداقل 3 حرف وارد نمایید').max(255).required('نام خانوادگی الزامی است'),
                    gender: Yup.mixed().oneOf(['Male', 'Female'], 'جنسیت الزامی است').required('جنسیت الزامی است'),
                    email: Yup.string().email('لطفاً یک ایمیل معتبر وارد نمایید')
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        fetch(`${ApiUrl}/api/user/profile`, {
                            method: 'PUT',
                            headers: AuthHeader,
                            body: JSON.stringify({
                                first_name: values.firstName,
                                last_name: values.lastName,
                                gender: values.gender,
                                email: values.email
                            })
                        })
                            .then((res) => res.json())
                            .then(
                                (result) => {
                                    const data = FetchData(result, setStatus, setErrors);
                                    if (result.success) {
                                        SetUserData(result.data);
                                    }
                                    setSubmitting(false);
                                },
                                // Note: it's important to handle errors here
                                // instead of a catch() block so that we don't swallow
                                // exceptions from actual bugs in components.
                                (error) => {
                                    SetError(error, setStatus, setErrors);
                                    setSubmitting(false);
                                }
                            );
                        /*
                        if (scriptedRef.current) {
                            setStatus({ success: true });
                            setSubmitting(false);
                        }
                        */
                    } catch (err) {
                        // console.error(err);
                        if (scriptedRef.current) {
                            SetError(err, setStatus, setErrors);
                            setSubmitting(false);
                        }
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, status }) => (
                    <form noValidate onSubmit={handleSubmit} {...others}>
                        <Grid container spacing={matchDownSM ? 0 : 1}>
                            <Grid item xs={12} sm={6}>
                                <CustomInput
                                    type="text"
                                    name="firstName"
                                    label="نام"
                                    value={values.firstName}
                                    error={touched.firstName && errors.firstName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomInput
                                    type="text"
                                    name="lastName"
                                    label="نام خانوادگی"
                                    value={values.lastName}
                                    error={touched.lastName && errors.lastName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    inputProps={{}}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomInput
                                    type="tel"
                                    name="mobile"
                                    label="شماره همراه"
                                    value={values.mobile}
                                    error={touched.mobile && errors.mobile}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomSelect
                                    name="gender"
                                    label="جنسیت"
                                    value={values.gender}
                                    error={touched.gender && errors.gender}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Male" key="0">
                                        مرد
                                    </MenuItem>
                                    <MenuItem value="Female" key="1">
                                        زن
                                    </MenuItem>
                                </CustomSelect>
                            </Grid>
                        </Grid>
                        <CustomInput
                            type="email"
                            name="email"
                            label="رایانامه"
                            value={values.email}
                            error={touched.email && errors.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                        />

                        {errors.submit && <SnackAlert message={errors.submit} type="error" />}

                        {status && status.success && <SnackAlert message="مشخصات کاربری با موفقیت ثبت شد!" type="success" />}

                        <Box sx={{ mt: 2 }}>
                            <AnimateButton>
                                <Button
                                    disableElevation
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                >
                                    ثبت مشخصات
                                </Button>
                            </AnimateButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default FirebaseProfile;
